import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next';
import styles from './Location.module.scss';
import { fetchData } from '@/services/fetchData';
import { PaginationRoutes } from '@/components/PaginationRoutes';
import { LocationType, SettingsType } from '@/types';
import { CardSize, CardType, TagPosition } from '@/components/Card';
import { MobileEffect, SliderBlock } from '@/components/SliderBlock';
import { LocationMap } from '@/components/LocationMap';
import { Layout } from '@/layout';
import { BLACK_COLOR, PROMENAD_CODE } from '@/constants';
import { Gallery } from '@/components/Gallery';
import { parseText } from '@/utils/parseText';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { getTextColor } from '@/utils/getTextColor';

const getEntertainmentsTitle = (
  count: number,
  locale?: string,
  customText?: string,
) => {
  if (customText) {
    return customText;
  }

  if (count < 5 && locale === 'ru') {
    return 'Развлечения';
  }

  if (count < 5 && locale !== 'ru') {
    return 'Things to Do';
  }

  if (count > 5 && locale !== 'ru') {
    return `Over ${Math.floor(count / 5) * 5} things to do`;
  }

  return `Более ${Math.floor(count / 5) * 5} развлечений`;
};

enum BLOCK_ARRAY_ELEMENT {
  Entertainments = 'entertainments',
  Trades = 'trades',
  Map = 'map',
}

const DEFAULT_BLOCK_ARRAY = [
  BLOCK_ARRAY_ELEMENT.Entertainments,
  BLOCK_ARRAY_ELEMENT.Trades,
  BLOCK_ARRAY_ELEMENT.Map,
];

const getBlockStructure = (blocks: string[]) => {
  if (blocks && Array.isArray(blocks)) {
    const blockMapping: { [key: string]: string } = {
      entertainments: BLOCK_ARRAY_ELEMENT.Entertainments,
      trades: BLOCK_ARRAY_ELEMENT.Trades,
      map: BLOCK_ARRAY_ELEMENT.Map,
    };

    const orderedBlocks = blocks
      .map((block) => blockMapping[block])
      .filter(Boolean);

    const missingBlocks = DEFAULT_BLOCK_ARRAY.filter(
      (block) => !orderedBlocks.includes(block),
    );

    return [...orderedBlocks, ...missingBlocks];
  }

  return DEFAULT_BLOCK_ARRAY;
};

const Location = ({
  content,
  menu,
  translations,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const itemData: LocationType = content.item;

  const backendHTML = { __html: itemData.text };
  const { locale } = useRouter();

  useEffect(() => {
    const color =
      itemData.ourObject && itemData.ourObject.colorStyles
        ? itemData.ourObject.colorStyles
        : BLACK_COLOR;

    if (color) {
      const styleTag = document.createElement('style');
      styleTag.textContent = getTextColor(color);
      document.head.appendChild(styleTag);

      return () => {
        document.head.removeChild(styleTag);
      };
    }
  }, [itemData.ourObject]);

  const orderedBlocks = getBlockStructure(itemData.blocks);

  return (
    <Layout data={menu} meta={content.meta}>
      <main className={styles.page}>
        <PaginationRoutes
          routes={[
            {
              link: itemData.code,
              text: itemData.name,
            },
          ]}
        />
        <Gallery
          galleryMob={itemData.galleryMob}
          gallery={itemData.gallery}
          title={itemData.name}
        />
        <div className="info" dangerouslySetInnerHTML={backendHTML} />
        {itemData.ourObject && itemData.ourObject.styles && (
          <div
            dangerouslySetInnerHTML={{ __html: itemData.ourObject.styles }}
          />
        )}
        {orderedBlocks.map((block) => {
          switch (block) {
            case 'entertainments':
              return (
                <>
                  {itemData.entertainment &&
                    itemData.entertainment.length > 0 && (
                      <SliderBlock
                        items={itemData.entertainment}
                        cardType={CardType.Panel}
                        button={translations.entertainmentsButton}
                        link="/entertainments"
                        size={CardSize.XS}
                        title={getEntertainmentsTitle(
                          itemData.entertainment.length,
                          locale,
                          translations.entertainmentsTitle,
                        )}
                        mobileEffect={MobileEffect.Cards}
                        tagPosition={TagPosition.Bottom}
                        noInfo
                      />
                    )}
                </>
              );
            case 'trades':
              return (
                <>
                  {itemData.trades && itemData.trades.length > 0 && (
                    <SliderBlock
                      items={itemData.trades}
                      cardType={CardType.Panel}
                      link={`/objects/${PROMENAD_CODE}`}
                      size={CardSize.XS}
                      title={parseText(translations.tradesTitle)}
                      mobileEffect={MobileEffect.Slider}
                    />
                  )}
                </>
              );
            case 'map':
              return (
                <>
                  {itemData.zoneLocation && (
                    <LocationMap
                      img={itemData.zoneLocation.picture}
                      file={itemData.zoneLocation.file}
                      mobileImg={itemData.zoneLocation.pictureMob}
                    />
                  )}
                </>
              );
            default:
              return null;
          }
        })}
      </main>
    </Layout>
  );
};

export async function getServerSideProps({
  params,
  locale,
}: GetServerSidePropsContext) {
  const paramsId = params?.location;

  const menu: SettingsType = await fetchData({
    url: `/settings/view`,
    locale,
  });

  const content = await fetchData({
    url: `/content/location/${paramsId}/view`,
    locale,
  });

  const isError = !content || content.code !== 200;

  if (isError) return { notFound: true };

  return {
    props: {
      content,
      menu,
      translations: menu.translations['/locations/[location]'],
    },
  };
}

export default Location;
